import React from 'react';
import { Link, Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, makeStyles, Typography } from '@popmenu/common-ui';
import { Skeleton } from '@material-ui/lab';
import { useRootPlaceBySlugData } from '../../hooks/data/useRootPlaceBySlugData';
import { dishCategoryFromSlug, useDishCategoryName } from '../../hooks/useDishCategoryName';
import { useRouteParams } from '../../hooks/useRouteParams';
import { DishCardsSkeleton } from '../DishCardsSkeleton';
import { DishFeedScroller } from '../DishFeedScroller';
import { Meta } from '../Meta';

const useStyles = makeStyles(theme => ({
  cardContainer: {
    [theme.breakpoints.down('md')]: {
      flex: `0 1 calc(33.33% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('sm')]: {
      flex: `0 1 calc(50% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('xs')]: {
      flex: '0 1 100%',
    },
    flex: `0 1 calc(25% - ${theme.spacing(3)}px)`,
    marginBottom: theme.spacing(6),
  },
  cardSkeleton: {
    borderRadius: theme.spacing(1),
    height: 0,
    paddingTop: '75%',
  },
  dishesContainer: {
    // Only show 1 row of skeleton cards based on screen size
    [theme.breakpoints.down('md')]: {
      '& .infinite-skeleton:nth-of-type(n+4)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .skeleton:nth-of-type(n+3)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('xs')]: {
      '& .infinite-skeleton:nth-of-type(n+2)': {
        display: 'none',
      },
    },
    '& > div': {
      alignItems: 'flex-start',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  },
  title: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    fontSize: '2rem',
    lineHeight: 'normal',
  },
  titleContainer: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
    borderBottom: `1px solid ${theme.palette.gray.light}`,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  titleLink: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: 'none',
  },
}));

export const DishCategoryPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const params = useRouteParams();
  const dishCategoryName = useDishCategoryName();
  const placeSlug = params.placeSlug ?? '';
  const categorySlug = params.categorySlug ?? '';
  const dishCategory = dishCategoryFromSlug(categorySlug);
  const categoryPath = `/places/${placeSlug}/categories/${categorySlug}`;

  const { data: placeData } = useRootPlaceBySlugData({
    dishCategory,
    limit: 12,
    offset: 0,
    slug: placeSlug,
    sortBy: 'popsCount',
    sortDir: 'desc',
  });
  const place = placeData?.rootPlace;

  if (!dishCategory) {
    return <Redirect to={`/places/${placeSlug}`} />;
  }

  if (!place) {
    return (
      <Box>
        <Box className={classes.titleContainer}>
          <Skeleton className={classes.title} />
        </Box>
        <Box className={classes.dishesContainer}>
          <div>
            <DishCardsSkeleton
              cardContainerClassName={classes.cardContainer}
              cardSkeletonClassName={classes.cardSkeleton}
              length={12}
            />
          </div>
        </Box>
      </Box>
    );
  }

  const categoryName = dishCategoryName(dishCategory);

  return (
    <Box>
      <Meta
        canonicalPath={categoryPath}
        title={intl.formatMessage({ defaultMessage: 'Trending {categoryName} near {placeName}' }, { categoryName, placeName: place.name })}
      />
      <Box className={classes.titleContainer}>
        <Typography className={classes.title} component="h1" variant="h1">
          <FormattedMessage
            defaultMessage={'Trending {categoryName} near {placeName}'}
            values={{
              categoryName,
              placeName: (
                <Link
                  className={classes.titleLink}
                  to={`/places/${placeSlug}`}
                >
                  {place.name}
                </Link>
              ),
            }}
          />
        </Typography>
      </Box>
      <DishFeedScroller
        dishCategory={dishCategory}
        dishPathPrefix={categoryPath}
        placeSlug={placeSlug}
      />
    </Box>
  );
};
